import { removeBackground } from '@imgly/background-removal';
import { keyOutHeadBackground } from './headBackgroundKey';

const MAX_SIDE = 1024;

/** @type {import('@imgly/background-removal').Config} */
const MATTE_CONFIG = {
  model: 'isnet_quint8',
  output: {
    format: 'image/png',
    quality: 0.95,
  },
};

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (e) => reject(e));
    image.src = src;
  });
}

function drawImageMaxSide(img, maxSide) {
  let rw = img.naturalWidth;
  let rh = img.naturalHeight;
  if (!rw || !rh) throw new Error('Invalid image dimensions');
  if (rw > maxSide) {
    rh = (rh * maxSide) / rw;
    rw = maxSide;
  }
  if (rh > maxSide) {
    rw = (rw * maxSide) / rh;
    rh = maxSide;
  }
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(rw));
  canvas.height = Math.max(1, Math.round(rh));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unsupported');
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  return canvas;
}

function canvasToObjectUrl(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('toBlob failed'));
        else resolve(URL.createObjectURL(blob));
      },
      'image/png',
      0.92
    );
  });
}

/** @imgly/background-removal only decodes Blob / URL / buffer — not HTMLCanvasElement. */
function canvasToPngBlob(canvas) {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) reject(new Error('toBlob failed'));
        else resolve(blob);
      },
      'image/png',
      0.95
    );
  });
}

/**
 * Neural background removal (in-browser). Falls back to heuristic keying if model fails
 * (offline, blocked WASM, etc.).
 */
async function matteCanvasWithFallback(canvas) {
  try {
    const pngBlob = await canvasToPngBlob(canvas);
    const blob = await removeBackground(pngBlob, MATTE_CONFIG);
    return URL.createObjectURL(blob);
  } catch (err) {
    console.warn('[headMatting] removeBackground failed, using heuristic fallback', err);
    const c = document.createElement('canvas');
    c.width = canvas.width;
    c.height = canvas.height;
    const ctx = c.getContext('2d');
    if (!ctx) throw new Error('Canvas unsupported');
    ctx.drawImage(canvas, 0, 0);
    keyOutHeadBackground(c);
    return canvasToObjectUrl(c);
  }
}

/** Cropped head canvas (e.g. from camera round crop) → PNG blob URL. */
export async function matteHeadFromCanvas(canvas) {
  return matteCanvasWithFallback(canvas);
}

/**
 * Full uploaded image (object URL or data URL) → scaled canvas → matted PNG blob URL.
 */
export async function processHeadImageToKeyedPngUrl(objectUrlOrDataUrl) {
  const img = await loadImage(objectUrlOrDataUrl);
  const canvas = drawImageMaxSide(img, MAX_SIDE);
  return matteCanvasWithFallback(canvas);
}
